import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";
import useSessionStateSync from "@hooks/useSessionStateSync";

import {
    faChevronLeft,
    faCircleCheck,
    faCircleXmark
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { CheckoutStateInterface } from "@ts/interfaces/Misc/state/CheckoutStateInterface";

import {
    checkoutAtom,
    checkoutTotalAmountAtom,
    couponAtom,
    createSubscriptionAtom,
    isPaymentPendingAtom,
    pageQueryParamsAtom,
    validateCouponAtom
} from "@atoms/checkoutAtom.ts";

import { formatPrice, getTrackingId } from "@utils/utils";

import { Alert } from "@components/Alert";
import { Button } from "@components/Button";
import { Collapsable } from "@components/Collapsable";
import { Text } from "@components/Text";

import CheckoutSummary from "./components/CheckoutSummary";

interface CheckoutInfoInterface {
    product: {
        name: string;
        price: number;
        grade: string;
        isEditable: boolean;
        isAlreadyPurchased: boolean;
    };
    addon: { name: string; price: number; isEditable: boolean } | null;
    discount: { name: string; price: number; isEditable: boolean } | null;
}

interface PropTypes {
    onApplyHundredPercentOff: (s: boolean) => void;
}

const CheckoutDetailsContainer = ({ onApplyHundredPercentOff }: PropTypes) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const { t } = useTranslation();

    // This is used globally
    const [coupon, setCoupon] = useAtom(couponAtom);

    const couponParam = searchParams.get("coupon");

    const [checkoutInfo, setCheckoutInfo] =
        useState<CheckoutInfoInterface | null>(null);
    const [couponInput, setCouponInput] = useState<string | null>(coupon);
    const [isCouponInputCollapsed, setIsCouponInputCollapsed] = useState(false);
    const [isNoticeCollapsed, setIsNoticeCollapsed] = useState(false);

    const [createSubscriptionState] = useAtom(createSubscriptionAtom);
    const [checkoutTotalAmount, setCheckoutTotalAmount] = useAtom(
        checkoutTotalAmountAtom
    );
    const [checkout] = useAtom(checkoutAtom);
    const [validateCouponState, validateCouponMutate] =
        useAtom(validateCouponAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [isPaymentPending] = useAtom(isPaymentPendingAtom);

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();

    const [, sessionState] = useSessionStateSync<CheckoutStateInterface>();

    // Internal to this component, being used to memoize the initially calculated total checkout amount
    const internalTotalAmountRef = useRef(0);

    // Being used to calculate the width of the error message
    const couponInputRef = useRef<HTMLInputElement>(null);

    const checkoutPurchasingAddon =
        sessionState.checkout?.checkoutPurchasingAddon ?? null;

    const resetPriceAndCoupon = (): void => {
        validateCouponState.reset();
        const newCheckoutInfo = {
            ...checkoutInfo
        } as CheckoutInfoInterface;
        newCheckoutInfo.discount = null;
        if (checkout.productFamily && checkout.plan && checkout.grade) {
            let totalAmount = 0;
            if (!sessionState.checkout?.checkoutPurchasingAddon)
                totalAmount += checkout.plan.price;
            if (checkout.addon) {
                totalAmount += checkout.addon.price;
            }
            setCheckoutTotalAmount(totalAmount);
        }
        setCheckoutInfo(newCheckoutInfo);
        onApplyHundredPercentOff(false);
    };

    const onChangeCoupon = (e: ChangeEvent<HTMLInputElement>) => {
        setCouponInput(e.target.value);
        resetPriceAndCoupon();
        if (e.target.value === "" || e.target.value === null) {
            setCoupon(null);
        }
    };

    const onApplyCoupon = () => {
        if (couponInput)
            validateCouponMutate([
                {
                    coupon: couponInput,
                    ...(checkoutPurchasingAddon ? { type: "charge" } : {})
                }
            ]);
    };

    const onEditAppliedDiscount = () => {
        setIsCouponInputCollapsed(true);
        couponInputRef.current?.focus();
    };

    const goBack = () => {
        if (
            checkout.plan?.addons &&
            // logical AND is short-circuiting
            checkout.plan.addons.every(a => !a.isIncluded)
        ) {
            navigate(
                routeWithQueryParams(CheckoutPageRoutesEnum.ADDON_SELECT_PAGE)
            );
        } else {
            // If the selected product family has one plan, the plan selection page will be skipped from showing.
            if (checkout.productFamily!.plans.length === 1) {
                navigate(
                    routeWithQueryParams(
                        CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                    )
                );
            } else {
                navigate(
                    routeWithQueryParams(
                        CheckoutPageRoutesEnum.PLAN_SELECT_PAGE
                    )
                );
            }
        }
    };

    useEffect(() => {
        const prepareCheckoutInfo = () => {
            // List out each product in cart, and calculate the total amount
            if (checkout.productFamily && checkout.plan && checkout.grade) {
                const checkoutInfo: CheckoutInfoInterface = {
                    product: {
                        name: checkout.productFamily.name,
                        price: checkout.plan.price,
                        grade: checkout.grade.name,
                        isEditable: true,
                        // Use session state
                        isAlreadyPurchased:
                            !!sessionState.checkout?.checkoutPurchasingAddon
                    },
                    addon: (() => {
                        const defaultIncludedAddon =
                            checkout.plan.addons?.find(a => a.isIncluded) ??
                            null;
                        if (checkout.addon) {
                            return {
                                name: checkout.addon.name,
                                price: checkout.addon.price,
                                isEditable: true
                            };
                        }
                        if (defaultIncludedAddon) {
                            return {
                                name: defaultIncludedAddon.name,
                                price: defaultIncludedAddon.price,
                                isEditable: false
                            };
                        }
                        return null;
                    })(),
                    discount: null
                };
                let totalAmount = 0;
                if (!sessionState.checkout?.checkoutPurchasingAddon)
                    totalAmount += checkout.plan.price;
                if (checkout.addon) {
                    totalAmount += checkout.addon.price;
                }
                setCheckoutTotalAmount(totalAmount);

                // Total amount is initially updated in the state, and also momoized
                setCheckoutInfo(checkoutInfo);
                internalTotalAmountRef.current = totalAmount;
            }
        };

        prepareCheckoutInfo();
    }, [checkout]);

    /**
     * "useEffect" hook to apply coupon passed via query parameter automatically.
     *
     * This also gets executed each time user lands on the payment page with a coupon
     * as we need to validate coupons as frequently as possible.
     */
    useEffect(() => {
        if (couponParam && validateCouponState.isIdle) {
            setCoupon(couponParam);
            setCouponInput(couponParam);
            setIsCouponInputCollapsed(true);
            onApplyCoupon();
        }
    }, [couponParam, validateCouponState]);

    useEffect(() => {
        const applyCoupon = () => {
            if (validateCouponState.isSuccess) {
                let discountedPrice = 0;

                const {
                    data: {
                        discount_type: discountType,
                        discount_amount: discountAmount,
                        discount_percentage: discountPercentage
                    }
                } = validateCouponState.data;
                if (discountType === "percentage") {
                    /**
                     * Scenario where the coupon provides a 100% discount.
                     */
                    let newDiscountPercentage = discountPercentage;
                    if (discountPercentage! >= 100) {
                        newDiscountPercentage = 100;
                        onApplyHundredPercentOff(true);
                    } else {
                        onApplyHundredPercentOff(false);
                    }

                    discountedPrice =
                        internalTotalAmountRef.current *
                        (newDiscountPercentage! / 100);
                }
                if (discountType === "fixed") {
                    discountedPrice = discountAmount!;
                }

                setCheckoutTotalAmount(
                    internalTotalAmountRef.current - discountedPrice
                );
                setCoupon(couponInput);

                const newCheckoutInfo = {
                    ...checkoutInfo
                } as CheckoutInfoInterface;
                newCheckoutInfo.discount = {
                    name: "Discount through coupon code",
                    price: discountedPrice,
                    isEditable: true
                };

                setCheckoutInfo(newCheckoutInfo);
            }

            if (validateCouponState.isError) {
                setCoupon(null);
                onApplyHundredPercentOff(false);
                setCheckoutTotalAmount(internalTotalAmountRef.current);

                const newCheckoutInfo = {
                    ...checkoutInfo
                } as CheckoutInfoInterface;
                newCheckoutInfo.discount = null;
                setCheckoutInfo(newCheckoutInfo);
            }

            // Update the related query parameters with the selected coupon.
            onSetupQueryParams({
                ...pageQueryParams,
                coupon
            });
        };

        applyCoupon();
    }, [validateCouponState, coupon]);

    return (
        <Flex
            bg={["white", null, null, "gray.50"]}
            direction="column"
            w="full"
            align="start"
            px={[0, null, null, 18, 20]}
        >
            <Flex
                px={3}
                gap={2}
                w="full"
                align="center"
                justify={["center", null, "start"]}
                mt={10}
            >
                <Box
                    className={ClassesEnum.Button}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.PaymentDetailsContainer,
                            ClassesEnum.Button
                        ],
                        "goBackFromPaymentPage"
                    )}
                    onClick={goBack}
                    cursor="pointer"
                    mt={-2}
                >
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                        color="var(--euka-colors-gray-900)"
                    />
                </Box>
                <Text m={["auto", null, 0]} textAlign="center" type="heading">
                    {t("checkout.checkoutPage.title", "Payment Details")}
                </Text>
            </Flex>
            <Flex
                border="1px solid"
                borderLeftWidth={[0, null, null, "1px"]}
                borderRightWidth={[0, null, null, "1px"]}
                borderColor="gray.400"
                direction="column"
                gap={4}
                py={6}
                px={5}
                bg="white"
                borderRadius={[0, null, null, 15]}
                w={["full", null, null, null, null, 700]}
                mt={8}
                ml={[0, null, "auto"]}
            >
                {checkoutInfo && (
                    <CheckoutSummary
                        product={checkoutInfo.product}
                        addon={checkoutInfo.addon}
                        discount={checkoutInfo.discount ?? undefined}
                        onEditAppliedDiscount={onEditAppliedDiscount}
                    />
                )}
                <Flex justify="space-between">
                    <Flex direction="column">
                        <Text
                            fontSize={18}
                            type="subHeading"
                            fontFamily="inter, sans-serif"
                        >
                            {t("checkout.checkoutPage.totalCost", "Total Cost")}
                        </Text>
                    </Flex>
                    <Flex
                        mt="auto"
                        justify="center"
                        align="center"
                        gap={4}
                        mr={7}
                    >
                        <Text
                            className={ClassesEnum.Text}
                            data-tracking-id={getTrackingId(
                                [
                                    ClassesEnum.PaymentDetailsContainer,
                                    ClassesEnum.Text
                                ],
                                "totalAmount"
                            )}
                            fontSize={18}
                            type="subHeading"
                            fontFamily="inter, sans-serif"
                        >
                            {formatPrice(checkoutTotalAmount)}
                        </Text>
                    </Flex>
                </Flex>
                <Text lineHeight="15px" color="gray.600" fontSize={12}>
                    Depending on your selected plan, it will automatically renew
                    either annually or term-by-term within a grade group, such
                    as Secondary. This does not apply to Grades 11 & 12. You
                    have the option to opt out anytime.
                </Text>
                <Text lineHeight="15px" color="gray.600" fontSize={12}>
                    Access to your learning program will be based on your
                    purchase date and your plan length, 365 days for an annual
                    plan or 90 days for a term plan.
                </Text>
                <Collapsable
                    isCollapsed={isCouponInputCollapsed}
                    onToggleCollapse={() =>
                        setIsCouponInputCollapsed(!isCouponInputCollapsed)
                    }
                    title="Add your coupon code"
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.PaymentDetailsContainer],
                        "addYourCouponCode"
                    )}
                >
                    <FormControl
                        className={ClassesEnum.FormField}
                        data-tracking-id={getTrackingId(
                            [
                                ClassesEnum.PaymentDetailsContainer,
                                ClassesEnum.FormField
                            ],
                            "inputCouponCode"
                        )}
                    >
                        <FormLabel fontSize={14}>
                            {t(
                                "checkout.checkoutPage.couponLabel",
                                "Coupon code"
                            )}
                        </FormLabel>
                        <Flex direction="column" gap={2}>
                            <Flex gap={2} mb={1} mx={1}>
                                <InputGroup>
                                    <Input
                                        className={ClassesEnum.Input}
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.PaymentDetailsContainer,
                                                ClassesEnum.FormField,
                                                ClassesEnum.Input
                                            ],
                                            "inputCouponCode"
                                        )}
                                        value={couponInput ?? ""}
                                        ref={couponInputRef}
                                        isInvalid={
                                            couponInput !== "" &&
                                            validateCouponState.isError
                                        }
                                        onChange={onChangeCoupon}
                                        color={
                                            validateCouponState.isSuccess
                                                ? "mintGreen.500"
                                                : "gray.800"
                                        }
                                        aria-success={
                                            validateCouponState.isSuccess
                                        }
                                        autoFocus
                                        isDisabled={
                                            isPaymentPending ||
                                            createSubscriptionState.isSuccess
                                        }
                                    />
                                    {!validateCouponState.isPending && (
                                        <InputRightElement>
                                            {validateCouponState.isSuccess && (
                                                <FontAwesomeIcon
                                                    icon={faCircleCheck}
                                                    color="var(--euka-colors-mintGreen-500"
                                                    fontSize={17}
                                                />
                                            )}
                                            {couponInput &&
                                                validateCouponState.isError && (
                                                    <FontAwesomeIcon
                                                        icon={faCircleXmark}
                                                        color="var(--euka-colors-sunsetRed-500"
                                                        fontSize={17}
                                                    />
                                                )}
                                        </InputRightElement>
                                    )}
                                </InputGroup>
                                <Button
                                    className={ClassesEnum.Button}
                                    data-tracking-id={getTrackingId(
                                        [
                                            ClassesEnum.PaymentDetailsContainer,
                                            ClassesEnum.Button
                                        ],
                                        "applyCouponCode"
                                    )}
                                    size="sm"
                                    colorScheme="ebonyClay"
                                    mt="auto"
                                    w={32}
                                    isDisabled={
                                        !couponInput ||
                                        isPaymentPending ||
                                        createSubscriptionState.isSuccess
                                    }
                                    isLoading={validateCouponState.isPending}
                                    onClick={onApplyCoupon}
                                >
                                    Apply
                                </Button>
                            </Flex>
                            {couponInput && validateCouponState.isError && (
                                <Alert
                                    type="error"
                                    message={
                                        (
                                            validateCouponState.error as {
                                                response: {
                                                    data: { message: string };
                                                };
                                            }
                                        ).response.data.message
                                    }
                                    w={
                                        couponInputRef.current
                                            ? couponInputRef.current.offsetWidth
                                            : "100%"
                                    }
                                />
                            )}
                        </Flex>
                    </FormControl>
                </Collapsable>
            </Flex>
            <Box
                borderBottom="1px solid"
                borderColor={["gray.400", null, null, "transparent"]}
                ml={[0, null, "auto"]}
                w={["full", null, null, null, null, 700]}
                py={6}
                px={[4, null]}
            >
                <Collapsable
                    isCollapsed={isNoticeCollapsed}
                    onToggleCollapse={() =>
                        setIsNoticeCollapsed(!isNoticeCollapsed)
                    }
                    title={
                        <Text color="royalblue">
                            Want to add another student?
                        </Text>
                    }
                >
                    <Text mb={4} fontSize={14}>
                        If you'd like to enrol additional students, simply
                        complete the purchase and enrolment for your first
                        student, and once you log into your Parent Portal,
                        you'll see the option to add more students.
                    </Text>
                </Collapsable>
            </Box>
        </Flex>
    );
};

export default CheckoutDetailsContainer;
